import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Card,
  CardBody,
  Col,
  Form,
  Input,
  Label,
  Row,
  FormFeedback,
  Spinner,
  Container,
  CardHeader,
} from "reactstrap";
import { Btn } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { baseUrl, apiPost, uploadFile, mediaUrl } from "../../Utils";
import axios, { AxiosError } from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import { parsePhoneNumberFromString, CountryCode } from "libphonenumber-js";
import { useAuth } from "../../Context/AuthContext";

interface FormValues {
  branch_name: string;
  branch_address: string;
  email: string;
  phone: string;
  status: string;
  manager_name: string;
  manager_contact: string;
  manager_email: string;
  branch_image: FileList | string; // Can be URL string or FileList
}

export default function EditBranch() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const {
    handleSubmit,
    setValue,
    control,
    formState: { errors }, reset
  } = useForm<FormValues>();
  const location = useLocation();
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const { hasPermission } = useAuth();
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const validatePhoneNumber = (value: string) => {
    if (!value) {
      return "Phone number is required.";
    }

    const sanitizedValue = value.replace(/\D/g, ""); // remove non-digits
    const phoneNumber = parsePhoneNumberFromString(`+${sanitizedValue}`);

    if (!phoneNumber || !phoneNumber.isValid()) {
      return "Invalid phone number.";
    }

    const allowedCountries: CountryCode[] = ["IN", "SA", "AE"];
    if (!allowedCountries.includes(phoneNumber.country as CountryCode)) {
      return "Only Indian, Saudi Arabian, or UAE numbers are allowed.";
    }

    return true;
  };

  useEffect(() => {
    const fetchBranchDetails = async () => {
      try {
        if (!location.state || !location.state.id) {
          toast.error("No Branch ID found");
          navigate(`${process.env.PUBLIC_URL}/branch`);
          return;
        }

        const result = await apiPost("branches/viewbyid", { id: location.state.id });

        setValue("branch_name", result.branch_name);
        setValue("branch_address", result.address);
        setValue("email", result.email);
        setValue("phone", result.phone); // PhoneInput expects string, this should be fine
        setValue("status", result.status);
        setValue("manager_name", result.manager_name);
        setValue("manager_contact", result.manager_contact); // Same here
        setValue("manager_email", result.manager_email);

        // Set existing image preview if available
        if (result.branch_image) {
          const imageUrl = `${mediaUrl}${result.branch_image}`;
          setPreviewImage(imageUrl);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching branch details:", error);
        toast.error("Failed to fetch branch details");
      }
    };
    fetchBranchDetails();
  }, [setValue, location.state, navigate]);

  const onSubmit = async (data: FormValues) => {
    setIsButtonDisabled(true);
    try {
      let branchImageName = "";
      if (data.branch_image && data.branch_image[0] instanceof File) {
        branchImageName = await uploadFile(data.branch_image[0]);
      }

      const formData = new FormData();
      formData.append("id", location.state.id);
      formData.append("branch_name", data.branch_name);
      formData.append("address", data.branch_address);
      formData.append("email", data.email);
      formData.append("phone", data.phone);
      formData.append("status", data.status);
      formData.append("manager_name", data.manager_name || "");
      formData.append("manager_contact", data.manager_contact || "");
      formData.append("manager_email", data.manager_email || "");

      if (branchImageName) {
        formData.append("branch_image", branchImageName);
      }

      await apiPost("branches/update", formData);

      toast.success("Branch updated successfully", { autoClose: 1000 });
      reset();
      navigate(`${process.env.PUBLIC_URL}/branch`);

    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred";
      toast.error(errorMessage, { autoClose: 1000 });
    } finally {
      setIsButtonDisabled(false);
    }
  };

  return (
    <div className='page-body'>
      <Breadcrumbs
        pageTitle="Edit Branch"
        parent="General"
        title="Edit Branch"
      />
      {loading ? <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '70vh' }}><Spinner color="primary" /></div> : (
        <Container fluid>
          <Col xl={12} className="mb-lg-3">
            <Card>
              <CardBody className="custom-input form-validation">
                <Form onSubmit={handleSubmit(onSubmit)} noValidate >
                  <Row className="g-3">
                    <Col md={6}>
                      <Label>Branch Name <span className="text-danger">*</span></Label>
                      <Controller
                        name="branch_name"
                        control={control}
                        rules={{ required: "Branch name is required" }}
                        render={({ field }) => (
                          <>
                            <Input
                              {...field}
                              placeholder="Enter Branch Name"
                              invalid={!!errors.branch_name}
                              style={{ borderColor: errors.branch_name ? "red" : "" }}
                              autoComplete="off"
                            />
                            <FormFeedback>
                              {errors.branch_name?.message}
                            </FormFeedback>
                          </>
                        )}
                      />
                    </Col>

                    <Col md={6}>
                      <Label>Branch Address <span className="text-danger">*</span></Label>
                      <Controller
                        name="branch_address"
                        control={control}
                        rules={{ required: "Address is required" }}
                        render={({ field }) => (
                          <>
                            <Input
                              type="textarea"
                              rows={1}
                              {...field}
                              placeholder="Enter Address"
                              invalid={!!errors.branch_address}
                              style={{ borderColor: errors.branch_address ? "red" : "" }}
                              autoComplete="new-password"
                            />
                            <FormFeedback>
                              {errors.branch_address?.message}
                            </FormFeedback>
                          </>
                        )}
                      />
                    </Col>

                    <Col md={6}>
                      <Label>Email <span className="text-danger">*</span></Label>
                      <Controller
                        name="email"
                        control={control}
                        rules={{ required: "Email is required" }}
                        render={({ field }) => (
                          <>
                            <Input
                              type="email"
                              {...field}
                              placeholder="Enter Email"
                              invalid={!!errors.email}
                              style={{ borderColor: errors.email ? "red" : "" }}
                              autoComplete="off"
                            />
                            <FormFeedback>
                              {errors.email?.message}
                            </FormFeedback>
                          </>
                        )}
                      />
                    </Col>

                    <Col md={6}>
                      <Label>Phone <span className="text-danger">*</span></Label>
                      <Controller
                        name="phone"
                        control={control}
                        rules={{
                          required: "Phone number is required",
                          validate: (value) => validatePhoneNumber(value),
                        }}
                        render={({ field }) => (
                          <>
                            <PhoneInput
                              country={"in"}
                              onlyCountries={["in", "sa", "ae"]}
                              disableDropdown={false}
                              countryCodeEditable={false}
                              value={field.value}
                              onChange={(value) => field.onChange(value)}
                              inputProps={{
                                name: "phone",
                                placeholder: "Enter phone number",
                                autoComplete: "none",
                              }}
                              inputStyle={{
                                backgroundColor: "var(--input-background-color)",
                                color: "var(--input-text-color)",
                                width: "100%",
                                borderColor: errors.phone
                                  ? "red"
                                  : "var(--input-border-color, #e0e0e0)",
                              }}
                              dropdownStyle={{
                                backgroundColor: "var(--input-background-color)",
                                backdropFilter: "blur(1000px)",
                              }}
                              buttonStyle={{
                                backgroundColor: "var(--input-background-color)",
                              }}
                              specialLabel=""
                            />
                            {errors.phone && (
                              <FormFeedback style={{ display: "block" }}>
                                {errors.phone.message}
                              </FormFeedback>
                            )}
                          </>
                        )}
                      />
                    </Col>

                    <Col xs={12}>
                      <Card className="mb-4" style={{ border: "1px solid #ced4da", borderRadius: "8px" }}>
                        <CardHeader tag="h5" style={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #ced4da", borderTopLeftRadius: "10px", borderTopRightRadius: "10px" }}>
                          Manager Details
                        </CardHeader>
                        <CardBody>
                          <Row className="g-3">
                            <Col md={4}>
                              <Label>Manager Name <span className="text-danger">*</span></Label>
                              <Controller
                                name="manager_name"
                                control={control}
                                rules={{ required: "Manager Name is required" }}
                                render={({ field }) => (
                                  <>
                                    <Input
                                      {...field}
                                      placeholder="Manager Name"
                                      invalid={!!errors.manager_name}
                                      style={{ borderColor: errors.manager_name ? "red" : "" }}
                                      autoComplete="off"
                                    />
                                    <FormFeedback>{errors.manager_name?.message}</FormFeedback>
                                  </>
                                )}
                              />
                            </Col>
                            <Col md={4}>
                              <Label>Manager Contact <span className="text-danger">*</span></Label>
                              <Controller
                                name="manager_contact"
                                control={control}
                                rules={{
                                  required: "Manager Contact is required",
                                  validate: (value) => validatePhoneNumber(value),
                                }}
                                render={({ field }) => (
                                  <>
                                    <PhoneInput
                                      country={"in"}
                                      onlyCountries={["in", "sa", "ae"]}
                                      disableDropdown={false}
                                      countryCodeEditable={false}
                                      value={field.value}
                                      onChange={(value) => field.onChange(value)}
                                      inputProps={{
                                        name: "manager_contact",
                                        placeholder: "Manager Contact",
                                        autoComplete: "none",
                                      }}
                                      inputStyle={{
                                        backgroundColor: "var(--input-background-color)",
                                        color: "var(--input-text-color)",
                                        width: "100%",
                                        borderColor: errors.manager_contact
                                          ? "red"
                                          : "var(--input-border-color, #e0e0e0)",
                                      }}
                                      dropdownStyle={{
                                        backgroundColor: "var(--input-background-color)",
                                        backdropFilter: "blur(1000px)",
                                      }}
                                      buttonStyle={{
                                        backgroundColor: "var(--input-background-color)",
                                      }}
                                      specialLabel=""
                                    />
                                    {errors.manager_contact && (
                                      <FormFeedback style={{ display: "block" }}>
                                        {errors.manager_contact.message}
                                      </FormFeedback>
                                    )}
                                  </>
                                )}
                              />
                            </Col>
                            <Col md={4}>
                              <Label>Manager Email</Label>
                              <Controller
                                name="manager_email"
                                control={control}
                                rules={{
                                  pattern: {
                                    value: /^\S+@\S+$/i,
                                    message: "Invalid email format",
                                  },
                                }}
                                render={({ field }) => (
                                  <>
                                    <Input
                                      type="email"
                                      {...field}
                                      placeholder="Manager Email"
                                      invalid={!!errors.manager_email}
                                      style={{ borderColor: errors.manager_email ? "red" : "" }}
                                      autoComplete="off"
                                    />
                                    <FormFeedback>{errors.manager_email?.message}</FormFeedback>
                                  </>
                                )}
                              />
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>
                    </Col>

                    <Col md={6}>
                      <Label>Status <span className="text-danger">*</span></Label>
                      <Controller
                        name="status"
                        control={control}
                        rules={{ required: "Status is required" }}
                        render={({ field }) => (
                          <>
                            <Input
                              type="select"
                              {...field}
                              invalid={!!errors.status}
                              style={{ borderColor: errors.status ? "red" : "" }}
                              autoComplete="off"
                            >
                              <option value="">Select Status</option>
                              <option value="Active">Active</option>
                              <option value="Inactive">Inactive</option>
                            </Input>
                            <FormFeedback>{errors.status?.message}</FormFeedback>
                          </>
                        )}
                      />
                    </Col>

                    <Col md={6}>
                      <Label>Branch Image</Label>
                      <Controller
                        name="branch_image"
                        control={control}
                        render={({ field: { value, onChange, ...field } }) => (
                          <>
                            <Input
                              type="file"
                              {...field}
                              onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                  const file = e.target.files[0];
                                  if (file.size > 2 * 1024 * 1024) {
                                    toast.error("File size must be less than 2MB");
                                    e.target.value = ""; // Clear input
                                    return;
                                  }
                                  onChange(e.target.files);
                                  setPreviewImage(URL.createObjectURL(file));
                                }
                              }}
                              accept="image/*"
                              autoComplete="off"
                            />
                            {previewImage && !(value instanceof FileList && value.length > 0) && (
                              <a href={previewImage} target="_blank" rel="noreferrer" className="d-block mt-2">View Current Document</a>
                            )}
                            {value instanceof FileList && value.length > 0 && (
                              <a href={URL.createObjectURL(value[0])} target="_blank" rel="noreferrer" className="d-block mt-2">View New Document</a>
                            )}
                          </>
                        )}
                      />
                    </Col>
                    {hasPermission('branch.edit') && (
                      <Col xs={12} className="mt-2">
                        <Btn color="primary" disabled={isButtonDisabled}>
                          {isButtonDisabled ? <>Updating <Spinner size="sm" /></> : "Update"}
                        </Btn>
                      </Col>
                    )}
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Container>
      )}
      <ToastContainer autoClose={1000} />
    </div>
  );
}
